import { createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";
import isAfter from "date-fns/isAfter";

export const cmsSlice = createSlice({
  name: "cms",
  initialState: {
    games: null,
    opponents: null,
    giveaways: null,
    homeBanner: null,
  },
  reducers: {
    setGames: (state, action) => {
      state.games = action.payload
        ?.filter(
          (game) =>
            game.active &&
            isAfter(
              new Date(`${game.date.replace(/-/g, "/")} ${game.date_time}`),
              new Date()
            )
        )
        ?.map((game) => {
          const _date = new Date(
            `${game.date.replace(/-/g, "/")} ${game.date_time}`
          ).toString();
          return { ...game, date: _date };
        });
    },
    setOpponents: (state, action) => {
      state.opponents = action.payload?.map((opponent) => {
        return opponent;
      });
    },
    setGiveaways: (state, action) => {
      state.giveaways = action.payload?.map((giveaway) => {
        return giveaway;
      });
    },
    setHomeBanner: (state, action) => {
      state.homeBanner = action.payload?.map((homeBanner) => {
        return homeBanner;
      });
    },
  },
});

export const { setGames, setOpponents, setGiveaways, setHomeBanner } =
  cmsSlice.actions;

// Games Getters
export const getGames = ({ cms }) => cms.games;
export const getGamesDates = ({ cms }) => {
  return cms.games?.map((game) => {
    return game.date;
  });
};
export const getHomeBanner = ({ cms }) => {
  return cms.homeBanner;
};
export const getGamesByGiveaways = ({ cms }) => {
  return cms.games?.filter((game) => {
    return game.giveaway?.length > 0;
  });
};
export const getUpcomingGames = ({ cms }) => {
  try {
    const _games = [...cms.games]
      ?.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
      })
      .slice(0, 8);
    return _games;
  } catch (error) {
    console.log("error", error);
  }
};
export const getGamesDateRange =
  (payload) =>
  ({ cms }) => {
    try {
      return [...cms?.games]
        ?.filter((d) => {
          if (!payload.start || !payload.end) return;

          const date = format(new Date(d.date), "MM/dd/yyyy");
          const startDate = format(new Date(payload.start), "MM/dd/yyyy");
          const endDate = format(new Date(payload.end), "MM/dd/yyyy");
          return startDate <= date && date <= endDate;
        })
        ?.sort(function (a, b) {
          return new Date(a.date) - new Date(b.date);
        });
    } catch (error) {
      console.log("error", error);
    }
  };
export const getGamesByOpponent =
  (opponent) =>
  ({ cms }) => {
    try {
      return [...cms?.games]
        ?.filter((d) => {
          if (!opponent) return;
          return d.opponent.alias === opponent;
        })
        ?.sort(function (a, b) {
          return new Date(a.date) - new Date(b.date);
        });
    } catch (error) {
      console.log("error", error);
    }
  };
export const getGamesByDayOfWeek = ({ cms }) => {
  var weekDays = {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  };
  try {
    Object.keys(weekDays).forEach((d) => {
      weekDays[d] = [...cms?.games]
        ?.filter((g) => {
          const date = format(new Date(g.date), "EEEE");
          return date === d;
        })
        ?.sort(function (a, b) {
          return new Date(a.date) - new Date(b.date);
        });
    });
    return weekDays;
  } catch (error) {
    console.log("error", error);
  }
};
export const getGamesByTimeOfDay = ({ cms }) => {
  var weekDays = {
    weekendDay: [],
    weekendEve: [],
    weekdayDay: [],
    weekdayEve: [],
  };
  try {
    Object.keys(weekDays).forEach((d) => {
      weekDays[d] = [...cms?.games]
        ?.filter((g) => {
          return g.time_of_day === d;
        })
        ?.sort(function (a, b) {
          return new Date(a.date) - new Date(b.date);
        });
    });
    return weekDays;
  } catch (error) {
    console.log("error", error);
  }
};
export const getGamesByPrice = ({ cms }) => {
  var price = {
    valueGames: [],
    regularGames: [],
    primeGames: [],
    marqueeGames: [],
    // diamondGames: [],
  };

  try {
    Object.keys(price).forEach((d) => {
      price[d] = [...cms?.games]
        ?.filter((g) => {
          return g.pricing === d;
        })
        ?.sort(function (a, b) {
          return new Date(a.date) - new Date(b.date);
        });
    });
    return price;
  } catch (error) {
    console.log("error", error);
  }
};

// Opponent Getters
export const getOpponentsByLeage = ({ cms }) => {
  if (!cms.opponents) return;

  try {
    function groupBy(arr, property) {
      return arr.reduce((acc, cur) => {
        acc[cur[property]] = [...(acc[cur[property]] || []), cur];
        return acc;
      }, {});
    }

    // console.log(Object.fromEntries(
    //   Object.entries(groupBy([...cms.opponents], "league")).sort(([, a], [, b]) => a - b)
    // ));
    return groupBy([...cms.opponents], "league");
  } catch (error) {
    console.log("error", error);
  }
};
export const getOpponents = ({ cms }) => {
  if (!cms.opponents) return;
  return [...cms.opponents].sort((a, b) => a.name.localeCompare(b.name));
};

// Giveaway getters
export const getGiveaways = ({ cms }) => {
  return cms.giveaways?.filter((giveaway) => giveaway.featured);
};
export const getGiveawaysTabs = ({ cms }) => {
  const allGamesWithGiveaways = cms.games?.filter((game) => {
    return game.giveaway?.length > 0;
  });
  return [
    ...cms.giveaways
      ?.filter((giveaway) => giveaway.featured)
      ?.map((giveaway) => {
        const { _id, name, image, alias, desc } = giveaway;
        const games = cms.games?.filter((game) => {
          return game.giveaway?.some((g) => g.alias === alias);
        });
        return {
          id: _id,
          label: name,
          bg: image,
          alias,
          desc,
          games,
        };
      }),
    {
      id: "all",
      label: "All",
      bg: {
        path: "cms/storage/uploads/2022/03/08/img-all_uid_6227cf25e2843.jpg",
      },
      games: allGamesWithGiveaways,
    },
  ];
};

export default cmsSlice.reducer;
